// Customer-facing Event Groups for the log viewer. Each group nests the raw
// function_name values that belong to it (from the product's group/function
// matrix). Selecting a group selects all of its functions; individual functions
// can also be toggled to build the query's function_name array.

export const EVENT_GROUPS = [
    {
        name: "Subscription Sign Up/Creation",
        description:
            "A new subscriber's internet service was set up, whether by our staff, the subscriber's self-checkout, or a property management system integration.",
        functions: [
            "vault_networkoperatorconsole_addKey",
            "vault_networkoperatorconsole_addKey_generic",
            "vault_networkoperatorconsole_addKey_stripe",
            "vault_realpageconnector_addSubscriber",
            "vault_realpageconnector_addSubscriber_stripe",
            "vault_shoppingcart",
            "vault_shoppingcart_approveSubscriptionSetup_addSubscriber",
            "vault_shoppingcart_stripe",
            "vault_shoppingcart_stripepayment_addSubscriber",
            "vault_shoppingcart_verifyPhoneNumber",
            "vault_venuemgmtconsole_addSubscriber",
            "vault_venuemgmtconsole_addSubscriber_generic",
            "vault_venuemgmtconsole_addSubscriber_stripe",
        ],
    },
    {
        name: "Subscription Date Changes",
        description:
            "A subscriber's service start date, end date, or move-out/cancellation date was changed.",
        functions: [
            "vault_networkoperatorconsole_resetSubscriptionEndDate",
            "vault_networkoperatorconsole_resetSubscriptionStartDate",
            "vault_realpageconnector_resetSubscriptionEndDate",
            "vault_subscriberconsole_cancelSubscription",
            "vault_venuemgmtconsole_resetSubscriptionEndDate",
            "vault_venuemgmtconsoleresetSubscriptionStartDate",
        ],
    },
    {
        name: "Subscriber Account & Data Deletion",
        description:
            "A subscriber's account, stored devices, or billing history were permanently deleted.",
        functions: [
            "/subscribers/forgetSubscriberDevices",
            "vault_networkoperatorconsole_deleteSubscriberAccountPermanently",
            "vault_networkoperatorconsole_deleteSubscriberTransactionsPermanently",
        ],
    },
    {
        name: "Subscriber Profile & Device Updates",
        description:
            "A subscriber's name, email, phone number, terms acceptance, or connected device name (e.g. renaming 'TV' or 'Ring Camera') was updated.",
        functions: [
            "/acceptTandC",
            "/subscribers/updateSubscriberPII",
            "/updateDeviceName",
            "/updatePII",
            "vault_subscriberconsole_updateDeviceName",
            "vault_subscriberconsole_updateKeyTag",
        ],
    },
    {
        name: "Subscriber Login & Password Resets",
        description:
            "A subscriber logged in (successfully or not) or reset their account password.",
        functions: [
            "/confirmResetConsolePassword",
            "subscriber_login_failed",
            "subscriber_login_success",
            "vault_subscriberconsole_initiateResetConsolePassword",
        ],
    },
    {
        name: "Subscriber E-mail/SMS Delivery",
        description:
            "An email or text message was sent to a subscriber (welcome messages, password reset links, etc.), including delivery/open confirmations.",
        functions: [
            "Email Delivery Log",
            "sendEmailtoSubscriber",
            "sendSMStoSubscriber",
        ],
    },
    {
        name: "Automated System Events",
        description:
            "Behind-the-scenes automated activity with no staff action involved: turning service up/down on schedule, network equipment health checks, Wi-Fi security syncing, property management system syncing, and internal system alerts.",
        functions: [
            "Realpage connector",
            "SubscriptionActivationScheduler",
            "VAULT v3 Network Equipment Configurator",
            "VAULT v3 Ruckus WPA3 Configurator",
            "VAULT v3 SIEM Notification",
            "VenueEquipmentUpdater",
            "VenueUnitCleaner",
        ],
    },
    {
        name: "Equipment & Network Setup Changes",
        description:
            "Staff manually added or updated a piece of network equipment (router, gateway, Wi-Fi controller) at a property.",
        functions: [
            "/venues/addVenueEquipment",
            "/venues/updateVenueEquipment",
        ],
    },
    {
        name: "Venue & Unit Setup/Changes",
        description:
            "A property or a unit within a property was added or its settings were configured.",
        functions: [
            "/venues/addVenue",
            "/venues/addVenueUnits",
            "/venues/configureVenue",
            "/venues/configureVenueUnit",
            "vault_venuemgmtconsole_configureVenue",
            "/venues/disableServicePlan",
            "/venues/enableServicePlan",
        ],
    },
    {
        name: "Venue & Unit Removal",
        description:
            "A property or a unit within a property was permanently deleted.",
        functions: [
            "/admin/deleteVenuePermanently",
            "/admin/deleteVenueUnitPermanently",
            "vault_networkoperatorconsole_deleteVenuePermanently",
            "vault_networkoperatorconsole_deleteVenueUnitPermanently",
        ],
    },
    {
        name: "Admin Creation",
        description:
            "A staff member's access/permissions to our system or to a specific property were granted or removed.",
        functions: [
            "vault_networkoperatorconsole_disableStaffAccess",
            "vault_networkoperatorconsole_enableStaffAccess",
            "vault_networkoperatorconsole_venue_disableStaffAccess",
            "vault_networkoperatorconsole_venue_enableStaffAccess",
            "vault_venuemgmtconsole_disableStaffAccess",
            "vault_venuemgmtconsole_enableStaffAccess",
        ],
    },
    {
        name: "Company Settings & Integrations",
        description:
            "Company-wide settings or third-party integrations (billing/ticketing systems, monitoring tools) were changed.",
        functions: [
            "/equipment/configureNetworkOperator",
            "/venues/addConnectWiseCredentials",
            "/venues/addPRTGCredentials",
            "/config/addRuckusR1Controller",
            "/config/configureRuckusR1Controller",
            "/equipment/addServicePlan",
            "/equipment/configureServicePlan",
            "vault_networkoperatorconsole_disableServicePlan",
            "vault_networkoperatorconsole_enableServicePlan",
            "/keypools/addKeypool",
            "/keypools/configureKeypool",
        ],
    },
];

// Maps each raw function_name to the scopes it appears in (from the
// category/function matrix). Scope keys match the LogViewer "logType" prop:
// "operator" | "venue" | "unit" | "subscriber" | "equipment".
export const FUNCTION_SCOPES = {
    "/acceptTandC": ["operator", "subscriber"],
    "/admin/deleteVenuePermanently": ["operator"],
    "/admin/deleteVenueUnitPermanently": ["operator", "venue"],
    "/config/addRuckusR1Controller": ["operator"],
    "/config/configureRuckusR1Controller": ["operator"],
    "/confirmResetConsolePassword": ["operator", "subscriber"],
    "/equipment/addServicePlan": ["operator"],
    "/equipment/configureNetworkOperator": ["operator"],
    "/equipment/configureServicePlan": ["operator"],
    "/keypools/addKeypool": ["operator"],
    "/keypools/configureKeypool": ["operator", "venue"],
    "/subscribers/forgetSubscriberDevices": ["operator", "subscriber"],
    "/subscribers/updateSubscriberPII": ["operator", "subscriber"],
    "/updateDeviceName": ["operator"],
    "/updatePII": ["operator"],
    "/venues/addConnectWiseCredentials": ["operator", "venue"],
    "/venues/addPRTGCredentials": ["operator", "venue"],
    "/venues/addVenue": ["operator", "venue"],
    "/venues/addVenueEquipment": ["operator", "venue", "equipment"],
    "/venues/addVenueUnits": ["operator", "venue", "unit"],
    "/venues/configureVenue": ["operator", "venue"],
    "/venues/configureVenueUnit": ["operator", "venue", "unit"],
    "/venues/disableServicePlan": ["operator", "venue"],
    "/venues/enableServicePlan": ["operator", "venue"],
    "/venues/updateVenueEquipment": ["operator", "venue", "equipment"],
    "Email Delivery Log": ["operator", "subscriber"],
    "Realpage connector": ["operator", "venue"],
    SubscriptionActivationScheduler: ["operator", "venue", "equipment"],
    "VAULT v3 Network Equipment Configurator": [
        "operator",
        "venue",
        "unit",
        "equipment",
    ],
    "VAULT v3 Ruckus WPA3 Configurator": ["operator", "venue"],
    "VAULT v3 SIEM Notification": ["operator", "venue", "equipment"],
    VenueEquipmentUpdater: ["operator", "venue", "equipment"],
    VenueUnitCleaner: ["operator", "venue", "unit"],
    sendEmailtoSubscriber: ["operator", "subscriber"],
    sendSMStoSubscriber: ["operator", "subscriber"],
    subscriber_login_failed: ["operator", "subscriber"],
    subscriber_login_success: ["operator", "subscriber"],
    vault_networkoperatorconsole_addKey: [
        "operator",
        "venue",
        "unit",
        "subscriber",
    ],
    vault_networkoperatorconsole_addKey_generic: ["operator", "venue"],
    vault_networkoperatorconsole_addKey_stripe: [
        "operator",
        "venue",
        "unit",
        "subscriber",
    ],
    vault_networkoperatorconsole_deleteSubscriberAccountPermanently: [
        "operator",
        "venue",
    ],
    vault_networkoperatorconsole_deleteSubscriberTransactionsPermanently: [
        "operator",
        "venue",
    ],
    vault_networkoperatorconsole_deleteVenuePermanently: ["operator"],
    vault_networkoperatorconsole_deleteVenueUnitPermanently: [
        "operator",
        "venue",
    ],
    vault_networkoperatorconsole_disableServicePlan: ["operator", "venue"],
    vault_networkoperatorconsole_disableStaffAccess: ["operator"],
    vault_networkoperatorconsole_enableServicePlan: ["operator", "venue"],
    vault_networkoperatorconsole_enableStaffAccess: ["operator"],
    vault_networkoperatorconsole_resetSubscriptionEndDate: [
        "operator",
        "venue",
        "unit",
        "subscriber",
    ],
    vault_networkoperatorconsole_resetSubscriptionStartDate: [
        "operator",
        "venue",
    ],
    vault_networkoperatorconsole_venue_disableStaffAccess: ["operator", "venue"],
    vault_networkoperatorconsole_venue_enableStaffAccess: ["operator", "venue"],
    vault_realpageconnector_addSubscriber: ["operator", "subscriber"],
    vault_realpageconnector_addSubscriber_stripe: ["operator", "subscriber"],
    vault_realpageconnector_resetSubscriptionEndDate: ["operator"],
    vault_shoppingcart: ["operator", "venue", "unit", "subscriber"],
    vault_shoppingcart_approveSubscriptionSetup_addSubscriber: [
        "operator",
        "venue",
        "unit",
        "subscriber",
    ],
    vault_shoppingcart_stripe: ["operator", "venue", "unit", "subscriber"],
    vault_shoppingcart_stripepayment_addSubscriber: [
        "operator",
        "venue",
        "unit",
        "subscriber",
    ],
    vault_shoppingcart_verifyPhoneNumber: [
        "operator",
        "venue",
        "unit",
        "subscriber",
    ],
    vault_subscriberconsole_cancelSubscription: [
        "operator",
        "venue",
        "unit",
        "subscriber",
    ],
    vault_subscriberconsole_initiateResetConsolePassword: [
        "operator",
        "subscriber",
    ],
    vault_subscriberconsole_updateDeviceName: ["operator", "subscriber"],
    vault_subscriberconsole_updateKeyTag: ["operator", "subscriber"],
    vault_venuemgmtconsole_addSubscriber: [
        "operator",
        "venue",
        "unit",
        "subscriber",
    ],
    vault_venuemgmtconsole_addSubscriber_generic: [
        "operator",
        "venue",
        "unit",
        "subscriber",
    ],
    vault_venuemgmtconsole_addSubscriber_stripe: [
        "operator",
        "venue",
        "unit",
        "subscriber",
    ],
    vault_venuemgmtconsole_configureVenue: ["operator", "venue"],
    vault_venuemgmtconsole_disableStaffAccess: ["operator", "venue"],
    vault_venuemgmtconsole_enableStaffAccess: ["operator", "venue"],
    vault_venuemgmtconsole_resetSubscriptionEndDate: [
        "operator",
        "venue",
        "unit",
        "subscriber",
    ],
    vault_venuemgmtconsoleresetSubscriptionStartDate: [
        "operator",
        "venue",
        "unit",
    ],
};

// Returns the Event Groups filtered to a scope: each group's functions are
// limited to those that appear in the given scope, and groups with no matching
// functions are dropped. Unknown functions (not in FUNCTION_SCOPES) are kept.
export const getEventGroupsForScope = (scope) => {
    if (!scope) return EVENT_GROUPS;
    return EVENT_GROUPS.map((group) => ({
        ...group,
        functions: group.functions.filter((fn) => {
            const scopes = FUNCTION_SCOPES[fn];
            return !scopes || scopes.includes(scope);
        }),
    })).filter((group) => group.functions.length > 0);
};

export default EVENT_GROUPS;
